import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";
import { useFormulaEvaluation } from "@/hooks/useFormulaEvaluation";
import { LinkedRecordCell } from "./LinkedRecordCell";
import { AttachmentUploader } from "./AttachmentUploader";
import { AttachmentGallery } from "./AttachmentGallery";
import { RecordComments } from "@/components/comments/RecordComments";
import { MessageSquare, Paperclip, FileText } from "lucide-react";

interface RecordEditorProps {
  record: any;
  fields: any[];
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  workspaceId?: string;
}

export const RecordEditor = ({ record, fields, open, onClose, onSave, workspaceId }: RecordEditorProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("fields");
  const [linkedRecords, setLinkedRecords] = useState<Record<string, string[]>>({});
  const [attachments, setAttachments] = useState<Record<string, any[]>>({});
  const [showAttachmentUploader, setShowAttachmentUploader] = useState<string | null>(null);
  const { evaluatedData, loading: formulaLoading } = useFormulaEvaluation(record?.id, fields);

  useEffect(() => {
    setFormData(record?.data || {});
    if (record?.id) {
      fetchLinkedRecords();
      fetchAttachments();
    }
  }, [record]);

  const fetchLinkedRecords = async () => {
    const linkFields = fields.filter(f => f.type === 'link_to_record');
    const cache: Record<string, string[]> = {};
    
    for (const field of linkFields) {
      const { data } = await supabase
        .from("record_links")
        .select("target_record_id")
        .eq("source_record_id", record.id)
        .eq("field_id", field.id);
      
      cache[field.id] = (data || []).map(d => d.target_record_id);
    }
    
    setLinkedRecords(cache);
  };

  const fetchAttachments = async () => {
    const attachmentFields = fields.filter(f => f.type === 'attachment');
    const cache: Record<string, any[]> = {};
    
    for (const field of attachmentFields) {
      const { data } = await supabase
        .from("attachments")
        .select("*")
        .eq("record_id", record.id)
        .eq("field_id", field.id);
      
      cache[field.id] = data || [];
    }
    
    setAttachments(cache);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { error } = await supabase
        .from("records")
        .update({ data: formData })
        .eq("id", record.id);

      if (error) throw error;
      toast.success("Record updated!");
      onSave();
    } catch (error: any) {
      handleError(error, "Failed to update record");
    } finally {
      setSaving(false);
    }
  };

  const handleLinkedRecordChange = async (fieldId: string, newLinkedIds: string[]) => {
    try {
      // Delete existing links
      await supabase
        .from("record_links")
        .delete()
        .eq("source_record_id", record.id)
        .eq("field_id", fieldId);

      // Insert new links
      if (newLinkedIds.length > 0) {
        const links = newLinkedIds.map(targetId => ({
          source_record_id: record.id,
          target_record_id: targetId,
          field_id: fieldId,
        }));

        const { error } = await supabase
          .from("record_links")
          .insert(links);

        if (error) throw error;
      }

      setLinkedRecords(prev => ({
        ...prev,
        [fieldId]: newLinkedIds,
      }));
    } catch (error: any) {
      handleError(error, "Failed to update links");
    }
  };

  const handleAttachmentUpload = async (fieldId: string, newAttachments: any[]) => {
    try {
      const attachmentRecords = newAttachments.map(a => ({
        record_id: record.id,
        field_id: fieldId,
        filename: a.filename,
        url: a.url,
        mime_type: a.mime_type,
        size: a.size,
      }));

      const { data, error } = await supabase
        .from("attachments")
        .insert(attachmentRecords)
        .select();

      if (error) throw error;

      setAttachments(prev => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...(data || [])],
      }));

      setShowAttachmentUploader(null);
    } catch (error: any) {
      handleError(error, "Failed to save attachments");
    }
  };

  const handleAttachmentRemove = async (fieldId: string, index: number) => {
    const attachment = attachments[fieldId]?.[index];
    if (!attachment) return;

    try {
      const { error } = await supabase
        .from("attachments")
        .delete()
        .eq("id", attachment.id);

      if (error) throw error;

      setAttachments(prev => ({
        ...prev,
        [fieldId]: prev[fieldId].filter((_, i) => i !== index),
      }));
    } catch (error: any) {
      handleError(error, "Failed to remove attachment");
    }
  };

  const renderFieldInput = (field: any) => {
    const value = formData[field.name] || "";

    // Formula fields are read-only
    if (field.type === "formula") {
      return (
        <div className="p-2 bg-muted rounded text-sm">
          {formulaLoading ? "Calculating..." : evaluatedData[field.name] || "—"}
        </div>
      );
    }

    // Linked record fields
    if (field.type === "link_to_record") {
      const targetTableId = field.config?.target_table_id;
      return (
        <LinkedRecordCell
          fieldId={field.id}
          sourceRecordId={record.id}
          targetTableId={targetTableId}
          linkedRecordIds={linkedRecords[field.id] || []}
          onLinksChange={(ids) => handleLinkedRecordChange(field.id, ids)}
          allowMultiple={field.config?.allow_multiple !== false}
        />
      );
    }

    // Attachment fields
    if (field.type === "attachment") {
      return (
        <div className="space-y-2">
          <AttachmentGallery
            attachments={attachments[field.id] || []}
            onRemove={(index) => handleAttachmentRemove(field.id, index)}
            onAddClick={() => setShowAttachmentUploader(field.id)}
          />
          {showAttachmentUploader === field.id && (
            <AttachmentUploader
              recordId={record.id}
              fieldId={field.id}
              onUploadComplete={(files) => handleAttachmentUpload(field.id, files)}
            />
          )}
        </div>
      );
    }

    switch (field.type) {
      case "long_text":
        return (
          <Textarea
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            rows={4}
          />
        );
      
      case "number":
      case "currency":
      case "percent":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: parseFloat(e.target.value) || 0 })}
          />
        );
      
      case "checkbox":
        return (
          <Checkbox
            checked={!!value}
            onCheckedChange={(checked) => setFormData({ ...formData, [field.name]: checked })}
          />
        );
      
      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
      
      case "email":
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
      
      case "phone":
        return (
          <Input
            type="tel"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
      
      case "url":
        return (
          <Input
            type="url"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
    }
  };

  // Count attachments for badge
  const totalAttachments = Object.values(attachments).flat().length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Edit Record</DialogTitle>
          <DialogDescription>Update the record fields and add comments</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 grid w-auto grid-cols-3">
            <TabsTrigger value="fields" className="gap-2">
              <FileText className="h-4 w-4" />
              Fields
            </TabsTrigger>
            <TabsTrigger value="attachments" className="gap-2">
              <Paperclip className="h-4 w-4" />
              Files
              {totalAttachments > 0 && (
                <span className="ml-1 text-xs bg-primary/20 px-1.5 rounded-full">
                  {totalAttachments}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Comments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fields" className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            <form onSubmit={handleSave} className="space-y-4">
              {fields.filter(f => f.type !== 'attachment').map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={field.id}>
                    {field.name}
                    {field.is_required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <div className="text-xs text-muted-foreground mb-1">{field.type.replace("_", " ")}</div>
                  {renderFieldInput(field)}
                </div>
              ))}
              <div className="flex gap-2 pt-4 sticky bottom-0 bg-background">
                <Button type="submit" disabled={saving} className="flex-1">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="attachments" className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            <div className="space-y-6">
              {fields.filter(f => f.type === 'attachment').length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Paperclip className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No attachment fields in this table</p>
                  <p className="text-sm">Add an attachment field type to enable file uploads</p>
                </div>
              ) : (
                fields.filter(f => f.type === 'attachment').map((field) => (
                  <div key={field.id} className="space-y-3">
                    <Label>{field.name}</Label>
                    <AttachmentGallery
                      attachments={attachments[field.id] || []}
                      onRemove={(index) => handleAttachmentRemove(field.id, index)}
                      onAddClick={() => setShowAttachmentUploader(field.id)}
                      maxDisplay={8}
                    />
                    {showAttachmentUploader === field.id ? (
                      <AttachmentUploader
                        recordId={record.id}
                        fieldId={field.id}
                        onUploadComplete={(files) => handleAttachmentUpload(field.id, files)}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAttachmentUploader(field.id)}
                        className="gap-2"
                      >
                        <Paperclip className="h-4 w-4" />
                        Upload Files
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="comments" className="flex-1 overflow-hidden px-0 pb-0 mt-0">
            {workspaceId ? (
              <RecordComments recordId={record.id} workspaceId={workspaceId} />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <p>Comments require workspace context</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
